namespace Ir.Components.ReportDesigner.ReportDesignerFieldList {

    interface IViewModel {
        title: string | KnockoutComputed<string>;
        fields: KnockoutObservableArray<ReportField | MetricField>;
        addButtonConfig: DevExpress.ui.dxButton.Properties;
        addExpressionButtonConfig: DevExpress.ui.dxButton.Properties;
        clearButtonConfig: DevExpress.ui.dxButton.Properties;
        sortableFieldsConfig: DevExpress.ui.dxSortable.Properties;
        scrollViewConfig: DevExpress.ui.dxScrollView.Properties;
        scrollViewControl: DevExpress.ui.dxScrollView;
        allowGroupId: KnockoutComputed<boolean>;
        groupList?: boolean;
        getFieldConfig: (reportField: ReportField | MetricField) => Ir.Components.ReportDesigner.ReportDesignerField.IParams;

        metricArray: KnockoutObservable<KnockoutObservableArray<MetricField>>;
        maxGroupId: KnockoutObservable<number>;
        checkMaxGroupId: Function;
        reportConfig: KnockoutObservable<ReportConfig>;
    }

    export interface IParams {
        title: string | KnockoutComputed<string>;
        fields: KnockoutObservableArray<ReportField | MetricField>;
        reportConfig: KnockoutObservable<ReportConfig>;
        widgetType: KnockoutObservable<WidgetType>;
        aceptMetrics: KnockoutObservable<boolean>;
        aceptFields: boolean;
        onAddFieldFromMapping?: (field: ReportField | MetricField) => void;
        onChanged?: Function;
        metricArray: KnockoutObservable<KnockoutObservableArray<MetricField>>;
        groupList?: boolean;
        hasComparisonFilters: KnockoutComputed<boolean>;
        groupFields: KnockoutObservableArray<ReportField>;
        openingAsPopup: boolean;
        reportDesigner: Ir.Components.ReportDesigner.ReportDesigner.IViewModel;
        showAddButton?: KnockoutObservable<boolean>;

        getAddFieldsFunctions: (onSelect: (item) => void) => void;
    }

    export function getViewModel(this: IViewModel, params: IParams, componentInfo) {
        let self = this;
        if ((params as any).config) {
            params = (params as any).config;
        }
        self.title = params.title;
        self.fields = params.fields;
        self.metricArray = params.metricArray;
        self.maxGroupId = ko.observable(0);
        self.reportConfig = params.reportConfig;
        self.groupList = params.groupList;

        self.allowGroupId = ko.computed(function() {
            if (params.widgetType() === WidgetType.Chart || params.widgetType() === WidgetType.Report && !params.groupList) {
                return false;
            }
            else {
                return true;
            }
        });

        if (params.aceptFields) {
            let checking = false;
            self.checkMaxGroupId = irGlobal.debounce(function() { checkMaxGroupIdSafe(self); checking = false; }, 300);
            checkMaxGroupId(self);
            self.fields.subscribe(function() {
                if (!checking) {
                    checking = true;
                    checkMaxGroupId(self);
                }
            });
        }

        if (!params.showAddButton) {
            params.showAddButton = ko.observable(true);
        }
        
        let aceptMetrics = params.aceptMetrics;
        let aceptFields = params.aceptFields;

        self.scrollViewConfig = { 
            direction: 'vertical', 
            onInitialized(e) {
                self.scrollViewControl = e.component;
            },
        };

        self.sortableFieldsConfig = {
            data: { fields: self.fields },
            filter: 'report-designer-field',
            itemOrientation: 'vertical',
            moveItemOnDrop: false,
            boundary: ".report-designer-container",
            elementAttr: {
                class: "sortable-openingTab"
            },
            group: "fields",
            onReorder(e) {
                if (e.toIndex >= 0) {
                    let toGroupId = self.fields()[e.toIndex].groupId();
                    let fromField = self.fields.splice(e.fromIndex, 1)[0];
                    fromField.groupId(toGroupId);
                    self.fields.splice(e.toIndex, 0, fromField);
                }
            },
            onAdd(e) {
                if (e.toIndex >= 0) {
                    //Dropped from mapping fields
                    if (e.fromData.selectedItem) {
                        let selectedItem = e.fromData.selectedItem() as FieldNameInputModel;
                        let isMetric = selectedItem.metric;
                        if (aceptMetrics() && isMetric || aceptFields && !isMetric) {
                            //Set groupId according to position or last field
                            let toGroupId = self.fields()[e.toIndex]?.groupId() ?? self.fields().at(-1)?.groupId() ?? 0;
                            let fromField = selectedItem.createMetricOrReportField(self.reportConfig().isOriginalTemplate);
                            if (!fromField.isMetric) {
                                //Default behavior asked by John to transform fields to group by
                                fromField.groupingType(GroupingTypeEnum.GROUP_BY);
                            }
                            fromField.groupId(toGroupId);
                            self.fields.splice(e.toIndex, 0, fromField);
                            params.onChanged?.();
                        }
                    }
                    else {
                        let fromFields = e.fromData.fields as KnockoutObservableArray<ReportField | MetricField>;
                        let isMetric = fromFields()[e.fromIndex].isMetric;
                        if (aceptMetrics() && isMetric || aceptFields && !isMetric) {
                            let fromField = fromFields.splice(e.fromIndex, 1)[0];
                            //Set groupId according to last field
                            fromField.groupId(self.fields().at(-1)?.groupId() ?? 0);
                            self.fields.splice(e.toIndex, 0, fromField);
                            params.onChanged?.();
                        }
                    }
                }
            },
        };

        self.addButtonConfig = {
            hint: "Add a new field",
            icon: "ms ir-add-icon",
            stylingMode: 'contained',
            type: "success",
            width: 24,
            height: 22,
            elementAttr: {
                class: "removePadding"
            },
            visible: params.showAddButton as any,
            onClick(e) {
                Ir.Components.ReportDesigner.ReportDesigner.hidePopovers();

                let typeToShow = MappingPopoverTypeEnum.NonMetrics;
                if (aceptMetrics()) {
                    if (aceptFields) {
                        if (self.reportConfig().isOriginalTemplate) {
                            typeToShow = MappingPopoverTypeEnum.AllNonDerivedDates;
                        }
                        else {
                            typeToShow = MappingPopoverTypeEnum.All;
                        }
                    }
                    else {
                        typeToShow = MappingPopoverTypeEnum.MetricsNode;
                    }
                }

                MappingService.show({
                    type: typeToShow,
                    $target: componentInfo.element,
                    onSelect(item: FieldNameInputModel) {
                        onSelect(item, self, params);
                    },
                    onSelectTable(fields: Array<FieldNameInputModel>) {
                        onSelectTable(fields, self, params);
                    },
                    fieldDragTemplate,
                    keepItOpened: true,
                    useOriginalName: self.reportConfig().isOriginalTemplate
                });
            }
        };

        self.addExpressionButtonConfig = {
            hint: "Add a new expression",
            text: "E+",
            elementAttr: {
                class: "add-expression removePadding"
            },
            width: 24,
            stylingMode: 'contained',
            type: "success",
            height: 22,
            visible: params.aceptMetrics as any,
            onClick(e) {
                let metricField = getNewMetricFieldExpression(self);
                params.metricArray().push(metricField);
                Ir.Components.ReportDesigner.ReportDesigner.hidePopovers();
                //Scroll down when adding
                let height = self.scrollViewControl.scrollHeight();
                self.scrollViewControl.scrollTo(height);
                params.onChanged?.();
                let groupingTypeOptions = Ir.Components.ReportDesigner.ReportDesignerField.allGroupingTypes.filter(x => x.forExpression);
                Ir.Components.ReportDesigner.ExpressionEditor.show(metricField, params.metricArray, groupingTypeOptions, params.reportDesigner);
            },
        };

        self.clearButtonConfig = {
            hint: "Clear fields",
            icon: "clearformat",
            stylingMode: 'contained',
            type: "danger",
            height: 22,
            width: 24,
            elementAttr: {
                class: "removePadding"
            },
            onClick(e) {
                Ir.Components.ReportDesigner.ReportDesigner.hidePopovers();

                self.fields.removeAll();
                params.onChanged?.();
            }
        };

        self.getFieldConfig = function(reportField: ReportField | MetricField) {
            return {
                field: reportField, 
                widgetType: params.widgetType, 
                reportConfig: params.reportConfig, 
                onRemove: function (field: ReportField | MetricField) {
                    self.fields.remove(field);
                    if (field.isMetric && (field as MetricField).metricContainer.comparisonType === ComparisonMetricType.None) {
                        let metricField = field as MetricField;
                        //Remove comparison variation metrics if they exists
                        self.fields.removeAll(self.metricArray()().filter(x => x.isMetric && x.parentMetricField == metricField &&
                            (x.metricContainer.comparisonType === ComparisonMetricType.Variation || x.metricContainer.comparisonType === ComparisonMetricType.PercentageVariation)
                        ));
                    }
                    params.onChanged?.();
                }, 
                onPropertiesHidden: function () {
                    params.onChanged?.();
                }, 
                metricArray: params.metricArray,
                onIncreaseGroupId(field: ReportField | MetricField) {
                    checkMaxGroupId(self);
                },
                maxGroupId: self.maxGroupId,
                onChange: params.onChanged,
                hasComparisonFilters: params.hasComparisonFilters,
                groupFields: params.groupFields,
                openingAsPopup: params.openingAsPopup,
                reportDesigner: params.reportDesigner
            } as Ir.Components.ReportDesigner.ReportDesignerField.IParams;
        }

        if (params.getAddFieldsFunctions) {
            params.getAddFieldsFunctions(
                (item: FieldNameInputModel) => {
                    onSelect(item, self, params);
                }
            );
        }
    }

    function addField(item: FieldNameInputModel, self: IViewModel, params: IParams) {
        let reportField = item.createMetricOrReportField(self.reportConfig().isOriginalTemplate);
        if (!reportField.isMetric) {
            //Default behavior is adding as GroupBy
            reportField.groupingType(GroupingTypeEnum.GROUP_BY);
            //Set groupId according to last field
            reportField.groupId(self.fields().at(-1)?.groupId() ?? 0);
        }
        self.fields.push(reportField);
        params.onAddFieldFromMapping?.(reportField);
    }

    function onSelect(item: FieldNameInputModel, self: IViewModel, params: IParams) {
        addField(item, self, params);
        let height = self.scrollViewControl.scrollHeight();
        self.scrollViewControl.scrollTo(height);
        params.onChanged?.();
    }

    function onSelectTable(fields: Array<FieldNameInputModel>, self: IViewModel, params: IParams) {
        for (let field of fields) {
            addField(field, self, params);
        }
        let height = self.scrollViewControl.scrollHeight();
        self.scrollViewControl.scrollTo(height);
        params.onChanged?.();
    }

    function checkMaxGroupId(self: IViewModel) {
        self.checkMaxGroupId();
    }

    function checkMaxGroupIdSafe(self: IViewModel) {
        let fields = self.fields().filter(x => !x.isMetric);
        let maxId = Math.max(...fields.map(x => x.groupId()));

        //Need at least two fields to change group
        if (!self.allowGroupId() || fields.length < 2) {
            self.maxGroupId(0);
        }
        else {
            if (fields.filter(x => x.groupId() == maxId).length > 1) {
                maxId++;
            }
            self.maxGroupId(maxId);
        }

        if (maxId > 0) {
            //Sort the array according to the groupId
            let newArray = (self.fields as any).sorted(function(left, right) {
                if (left.groupId() < right.groupId()) {
                    return -1;
                }
                if (left.groupId() == right.groupId()) {
                    return 0;
                }
                return 1;
            }) as Array<ReportField | MetricField>;

            //Check if a groupId is missing and reorganize fields
            let distinctGroups = [...new Set(newArray.map(x => x.groupId()))];
            let reorganizeFields = distinctGroups.length < maxId + 1;
            if (reorganizeFields) {
                let newgroupId = 0;
                let previousGroupId = newArray[0].groupId();
                for(let field of newArray) {
                    if(field.groupId() !== previousGroupId) {
                        newgroupId++;
                        previousGroupId = field.groupId();
                    }
                    field.groupId(newgroupId);
                }
            }

            //Check if the order was changed and apply to the observable array
            let newOrder = newArray.map(x => x.queryName).join(', ');
            let currentOrder = self.fields().map(x => x.queryName).join(', ');
            if (newOrder !== currentOrder) {
                self.fields(newArray);
                if (!self.groupList) {
                    self.reportConfig().fields(newArray);
                }
            }
        }
    }

    function getNewMetricFieldExpression(self: IViewModel) {
        let id = getExpressionId(self);

        let data = {
            id: null,
            field: null,
            autoGrouping: AutoGroupingEnum.None,
            groupingType: GroupingTypeEnum.DSUM,
            isReportMetric: true,
            visible: true,
            color: null,
            titleAlign: TextAlignEnum.Auto,
            valuesAlign: TextAlignEnum.Auto,
            orderIndex: null,
            order: 0,
            groupId: null,
            width: null,
            partitionBy: false,
            customDisplayName: "New Expression",
            displayName: null,
            numberFormat: FieldFormatEnum.Numeric,
            decimalPlaces: 2,
            accountingStyle: false,
            backgroundColor: null,
            foreColor: null,
            thresholdValue: null,
            thresholdColor: null,
            isChecked: true,
            useFormattingRuleFromMetric: true,
            formattingRule: null,
            visualizationModeDefinition: null,
            showSubtotalValue: true,
            showTotalValue: true,
            calculateTotals: CalculateTotalsEnum.UseFormula,
            metricFormat: {
                format: FieldFormatEnum.Numeric,
                decimalPlaces: 2,
                accountingStyle: false,
            },
            metricContainer: {
                metric: id,
                isExpression: true,
                comparisonType: ComparisonTypeEnum.None,
                comparisonFilterId: null,
                functionType: FunctionTypeEnum.Normal,
                metricExpression: {
                    id: id,
                    table: null,
                    metricName: "New Expression",
                    formulaTop: "",
                    formulaBottom: "",
                    calculateTotals: CalculateTotalsEnum.UseFormula,
                    groupingType: GroupingTypeEnum.DSUM,
                    format: FieldFormatEnum.Numeric,
                    decimalPlaces: 2,
                    foreColor: null,
                    backgroundColor: null,
                    thresholdValue: null,
                    thresholdColor: null,
                    formattingRule: null,
                    order: null,
                    accountingStyle: false,
                    visibility: VisibilityEnum.Normal,
                },
            },

        } as any;


        let metricOrReportField = new MetricField(data);  
        return metricOrReportField;
    }

    function getExpressionId(self: IViewModel) {
        var availableFields = self.metricArray()().filter(x => x.isMetric && x.metricContainer.isExpression);
        
        let id = -1;

        if (availableFields.length > 0) {
            id = Math.min(...availableFields.map(x => x.metricContainer.metric));
            id--;
        }

        return id;
    }

    function fieldDragTemplate(e, selectedItem: FieldNameInputModel) {
        let icon = selectedItem.metric ? "speed_1_2" : "abc";
        return $(`
<div class="report-designer-field">
    <div class="field">
        <div class="field-type">
            <span class="ms">${icon}</span>
        </div>
        <div class="field-name">
            <span>${selectedItem.displayName}</span>
        </div>
    </div>
</div>`);
    }


}